import React, { useContext, useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import Button from 'ls-common-client/dist/components/Button';
import NotificationSlider from '../NotificationSlider';
import Confirmation from './Confirmation';
import Dialog from '../Dialog';
import AlertPopup from '../AlertPopup';
import { Context } from '../../context/AppContext';
import { MODALS } from '../../constants';

const StyledDialog = styled(Dialog)`
  @media ${props => props.theme.mediaQueries.mobile} {
    border-radius: 0;
  }
`;

const StyledRenderContainer = styled(Container)`
  @media ${props => props.theme.mediaQueries.mobile} {
    &.DialogForm__Overlapped {
      padding-bottom: 100px;
    }
  }
`;

const StyledReturnButton = styled(Button)`
  min-width: 100px;
  height: 40px !important;
  margin: 16px auto 50px auto !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 22px;
  border: solid 1px #c4c4c4 !important;
  font-weight: normal;
`;

const DialogForm = ({
  className,
  name,
  show,
  children,
  notificationSlider,
  onDialogFormClose = () => {},
  ...props
}) => {
  const confirmationModalName = MODALS.MODAL_CONFIRMATION;

  const {
    modal: { showModal, hideModal, modals },
    routeHistory,
    media: { mobile },
    error: { error },
  } = useContext(Context);
  const [currentHistory] = routeHistory || [];
  const { route } = currentHistory || {};

  const { show: confirmationShow } = modals[confirmationModalName] || {};

  const [overlapped, setOverlapped] = useState();
  const [renderRef, setRenderRef] = useState();

  const onClose = () => {
    hideModal(confirmationModalName);
    setTimeout(() => onDialogFormClose(), 200);
  };

  const { button, buttonProps, text, icon } = notificationSlider || {};

  const calculateHeight = useCallback(() => {
    if (renderRef) {
      try {
        const { height } = renderRef.getBoundingClientRect();
        const fadeContainer =
          renderRef.parentElement.parentElement.parentElement;

        if (height >= window.innerHeight) {
          if (fadeContainer) {
            fadeContainer.style.alignItems = 'flex-start';
            fadeContainer.style.paddingTop = '0px';
            fadeContainer.style.overflowY = 'scroll';
            setOverlapped(true);
          }
        } else {
          if (fadeContainer) {
            fadeContainer.style.alignItems = 'center';
            fadeContainer.style.overflowY = null;
          }
          setOverlapped(false);
        }
        // eslint-disable-next-line no-empty
      } catch (e) {}
    }
  }, [renderRef]);

  useEffect(() => {
    setTimeout(() => {
      if (renderRef) calculateHeight();
    }, 500);
  }, [renderRef]);

  useEffect(() => {
    window.addEventListener('resize', calculateHeight);
    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);

  return (
    <>
      <StyledDialog
        {...props}
        data-route={route}
        className={classNames('DialogForm__Root', {
          [`DialogForm__${name}`]: name,
          [className]: className,
        })}
        animationDuration={300}
        show={show}
        render={({ children: child }) => (
          <StyledRenderContainer
            className={`DialogForm__Render ${
              overlapped ? 'DialogForm__Overlapped' : ''
            }`}
            ref={ref => {
              setRenderRef(ref);
            }}
          >
            {child}
          </StyledRenderContainer>
        )}
        onClose={() => {
          if (!error) showModal(confirmationModalName);
        }}
      >
        <>
          {children}
          <Confirmation
            show={confirmationShow}
            heading="Are you sure you want to quit?"
            text="If you quit now, this form will reset and you'll have to fill it in again."
            buttons={[
              {
                children: 'Quit',
                props: {
                  backgroundColor: 'white',
                  rounded: true,
                  color: 'normal',
                  width: '80px',
                  minWidth: '80px',
                  marginRight: 'auto',
                  fontWeight: 'normal',
                  onClick: () => onClose(),
                },
              },
              {
                children: "No, I'm not done yet!",
                props: {
                  primary: true,
                  rounded: true,
                  width: '200px',
                  minWidth: '200px',
                  onClick: () => hideModal(confirmationModalName),
                },
              },
            ]}
          />
          <AlertPopup />
        </>
      </StyledDialog>
      {!!notificationSlider && (
        <NotificationSlider
          {...notificationSlider}
          icon={
            icon ? (
              <Container
                paddingLeft={mobile ? '0' : 'normal'}
                paddingTop={mobile ? 'medium' : 'normal'}
                paddingBottom={mobile ? '0' : 'normal'}
              >
                {icon}
              </Container>
            ) : null
          }
          text={
            text || button ? (
              <>
                {text}{' '}
                {button && !!mobile ? (
                  <StyledReturnButton rounded {...buttonProps}>
                    {button}
                  </StyledReturnButton>
                ) : null}
              </>
            ) : null
          }
        />
      )}
    </>
  );
};

export default DialogForm;
